<template>
  <graphql-requester
    :gql="$options.CONFIRM_APPOINTMENT"
    :variables="{ input }"
    v-slot="{ data, loading }"
    @result="onResult"
    @error="onError"
  >
    <div role="region" aria-label="Appointment confirmation status">
      <div class="max-w-screen-sm mx-auto text-center">
        <template v-if="loading">
          <svg-loading aria-hidden="true" role="img"></svg-loading>
          <h3 class="text-2xl font-bold" role="status">Submitting your appointment...</h3>
        </template>
        <template v-else-if="data && !loading">
          <poll-status
            v-if="isAutoConfirm && hasNotLoaded"
            :status-url="data.confirmAppointment.status_url"
            @success="hasNotLoaded = false"
            @error="onPollStatusError"
          ></poll-status>
          <template v-else-if="error_message">
            <svg-error
              width="50"
              height="50"
              class="mx-auto mb-4"
              aria-hidden="true"
              role="img"
            ></svg-error>
            <p class="text-xl font-bold" role="alert">Error</p>
            <p role="alert">
              {{
                error_message || "There has been an error. Please try again."
              }}
            </p>
            <button-primary
              type="button"
              class="px-10 py-3 mt-10"
              @click="$emit('on:reset')"
              aria-label="Try submitting appointment again"
            >
              Try Again
            </button-primary>
          </template>
          <template v-else>
            <iframe
              v-if="isAutoConfirm"
              :src="thankyouUrl"
              frameborder="0"
              width="100%"
              height="700"
              title="Appointment confirmation page"
              aria-label="Appointment confirmation details"
            ></iframe>
            <template v-else>
              <svg-complete
                class="mx-auto"
                aria-hidden="true"
                role="img"
              ></svg-complete>
              <h3 class="mt-10 text-4xl font-bold" role="status">
                {{ $store.state.settings.verbiage.almost_done }}
              </h3>
              <p class="mt-4 text-lg">
                {{ toConfirm }}
                <strong class="block mt-2">{{ needToClick }}</strong>
              </p>
              <template v-if="hasSentVerbiage">
                <p class="mt-4 md:text-lg" role="status">
                  {{ $store.state.settings.verbiage.was_sent }}
                </p>
                <p class="text-lg md:text-2xl" aria-label="Confirmation email sent to">{{ input.email }}</p>
              </template>
              <p class="mt-4" role="alert">
                Please click the link in the email we just sent you within
                <strong>{{
                  $store.state.settings.confirm_token_expiry_in_min
                }}</strong>
                minutes to confirm your appointment.
              </p>
            </template>
          </template>
        </template>
        <template v-else>
          <template v-if="isPatientBlocked">
            <svg-offline
              width="50"
              height="50"
              class="mx-auto mb-4"
              aria-hidden="true"
              role="img"
            ></svg-offline>
            <h3 class="text-3xl font-bold mt-6" role="alert">
              We cannot schedule your appointment.
            </h3>
            <p class="mt-2">
              For details, please call {{ control.locations.name }} at
              <a
                class="underline"
                :href="`tel:${control.locations.contact_no}`"
                aria-label="Call location phone number"
                >{{ control.locations.contact_no }}</a
              >
            </p>
            <button-primary
              type="button"
              class="px-10 py-3 mt-10"
              @click="$emit('on:reset')"
              aria-label="Try scheduling appointment again"
            >
              Try Again
            </button-primary>
          </template>
          <template v-else>
            <svg-error
              width="50"
              height="50"
              class="mx-auto mb-4"
              aria-hidden="true"
              role="img"
            ></svg-error>
            <p role="alert">{{ errorMessage }}</p>
            <div
              v-if="internalErrors.validation"
              class="p-2 mt-10 text-red-400 bg-gray-100"
              role="alert"
              aria-label="Validation errors"
            >
              <ul v-for="(validation, i) in internalErrors.validation" :key="i">
                <li v-for="(error, j) in validation" :key="j">
                  {{ error | cleanError }}
                </li>
              </ul>
            </div>
            <button-primary
              type="button"
              class="px-10 py-3 mt-10"
              @click="$emit('on:again')"
              aria-label="Try submitting appointment again"
            >
              Try Again
            </button-primary>
          </template>
        </template>
      </div>
    </div>
  </graphql-requester>
</template>

<script>
import { CONFIRM_APPOINTMENT } from "@/graphql";
import { formatDate } from "@/helper";
import Base from "@/components/StepBase";
import GraphqlRequester from "@/components/GraphqlRequester";
import SvgComplete from "@/components/Svg/SvgComplete";
import SvgLoading from "@/components/Svg/SvgLoading";
import SvgError from "@/components/Svg/SvgError";
import SvgOffline from "@/components/Svg/SvgOffline";
import ButtonPrimary from "@/components/Button/ButtonPrimary";
import PollStatus from "@/components/PollStatus";

export default {
  CONFIRM_APPOINTMENT,
  extends: Base,
  components: {
    SvgComplete,
    SvgLoading,
    GraphqlRequester,
    ButtonPrimary,
    PollStatus,
    SvgError,
    SvgOffline,
  },
  filters: {
    cleanError(el) {
      return el.replace(/input.insurance./i, "");
    },
  },
  data() {
    return {
      hasNotLoaded: true,
      error_message: null,
      internalErrors: null,
      settings: null,
    };
  },
  watch: {
    hasNotLoaded(value) {
      if (!value && this.isAutoConfirm) {
        this.onNextStep(this.locationId);

        // Emit to `init.js` if booking is successful
        this.onBookSuccess();
      }
    },
  },
  computed: {
    thankyouHtml() {
      return this.settings?.thankyou_html || "";
    },
    thankyouUrl() {
      return this.settings?.thankyou_url || "";
    },
    isAutoConfirm() {
      return this.settings?.verification === "auto_confirm";
    },
    hasSentVerbiage() {
      return this.settings?.verbiage?.was_sent;
    },
    toConfirmed() {
      const to_confirm = this.settings.verbiage.to_confirm || "";
      const index = to_confirm.indexOf("[");
      let segment = {};
      segment.first = to_confirm.slice(0, index - 1);
      segment.last = to_confirm
        .slice(index)
        .replaceAll("[location]", this.locationName)
        .replaceAll("[datetime]", this.dateTime);
      return segment;
      // return first.concat("<br>", second);
    },
    toConfirm() {
      return (this.settings.verbiage.to_confirm || "")
        .replaceAll("[location]", this.locationName)
        .replaceAll("[datetime]", this.dateTime);
    },
    needToClick() {
      return (this.settings.verbiage.need_to_click || "")
        .replaceAll("[location]", this.locationName)
        .replaceAll("[datetime]", this.dateTime);
    },
    locationName() {
      return this.control.locations?.name || "Unknown Location";
    },
    locationId() {
      return (
        this.control.locations?.location_id || this.$store.getters.locationId
      );
    },
    dateTime() {
      const dt = this.input.appointment_datetime;
      if (!dt) return "";
      return formatDate(dt, "ddd - MMMM DD, YYYY @ h:mm A");
    },
    errorMessage() {
      return (
        this.errors?.message ||
        "An error occured while submitting your appointment."
      );
    },
    isPatientBlocked() {
      if (!this.errorMessage) return;
      return new RegExp("\\b" + "no permission" + "\\b").test(
        this.errorMessage
      );
    },
  },
  methods: {
    onPollStatusError(error_message) {
      this.hasNotLoaded = false;
      this.error_message = error_message;
    },
    onResult(data) {
      if (!data) return;

      this.settings = data?.confirmAppointment?.settings;

      if (!this.isAutoConfirm) {
        this.onNextStep(this.locationId);

        // Emit to `init.js` if booking is successful
        this.onBookSuccess();
      }
    },
    onError(error) {
      const parsedError = JSON.parse(error);
      const response = parsedError.response;
      const errors = response.errors[0];
      this.internalErrors = {};

      if (errors?.extensions?.validation) {
        this.internalErrors.validation = Object.values(
          errors.extensions.validation
        );
      }

      if (errors?.message) {
        this.internalErrors.message = errors.message;
      }
      this.$emit("on:error", response);
    },
    onNextStep(location_id) {
      this.$emit("on:nextstep", {
        event: "scheduling-success",
        mode: "sameday",
        location_id,
        value: 3,
      });
    },
    onBookSuccess() {
      this.$emit("on:book_success", {
        event: "scheduling-book-success",
        location: this.control?.locations,
        companyId: this.$store.state?.config?.companyId,
        totalBookingTimeInSeconds:
          this.$store.state?.meta?.totalBookingTimeInSeconds,
      });
    },
  },
  created() {
    this.$store.commit("fieldUpdate", {
      key: "isComplete",
      value: true,
    });
  },
  destroyed() {
    this.$store.commit("fieldUpdate", {
      key: "isComplete",
      value: false,
    });
  },
};
</script>
